import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiX, FiCheck, FiAlertCircle, FiExternalLink, FiYoutube, FiFileText } from 'react-icons/fi';
import nacmeLogo from './assets/nacme_logo.jpg';
import tlaLogo from './assets/tla_logo.png';
import { IS_TLA, PATHS } from './utils/platformPaths';

const AlumniNewsCuration = () => {
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('google_news'); // 'google_news' or 'youtube'

  // Data state
  const [googleNewsData, setGoogleNewsData] = useState(null);
  const [youtubeData, setYoutubeData] = useState(null);
  const [topics, setTopics] = useState([]);
  const [refreshStatus, setRefreshStatus] = useState(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('all');

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [commentary, setCommentary] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [sharedContentIds, setSharedContentIds] = useState(new Set());

  useEffect(() => {
    checkAccess();
    fetchTopics();
    fetchRefreshStatus();

    if (activeTab === 'google_news') {
      fetchGoogleNews();
    } else {
      fetchYouTube();
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'google_news' && !googleNewsData) {
      fetchGoogleNews();
    } else if (activeTab === 'youtube' && !youtubeData) {
      fetchYouTube();
    }
  }, [activeTab]);

  const checkAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(PATHS.login);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate(PATHS.login);
          return;
        }
        throw new Error('Failed to fetch profile');
      }

      const profile = await response.json();

      // Check if user has access to news curation
      if (!profile.can_access_news_curation) {
        setError('You do not have permission to access this feature. Contact an administrator if you believe this is an error.');
        return;
      }

    } catch (err) {
      console.error('Error checking access:', err);
      setError('Failed to verify access permissions.');
    }
  };

  const fetchTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

      const response = await fetch(`${backendUrl}/api/alumni/news-curation/topics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTopics(data.topics || []);
      }
    } catch (err) {
      console.error('Error fetching topics:', err);
    }
  };

  const fetchRefreshStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

      const response = await fetch(`${backendUrl}/api/alumni/news-curation/refresh-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRefreshStatus(data);
      }
    } catch (err) {
      console.error('Error fetching refresh status:', err);
    }
  };

  const fetchGoogleNews = async (forceRefresh = false) => {
    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

      const url = forceRefresh
        ? `${backendUrl}/api/alumni/news-curation/google?force_refresh=true`
        : `${backendUrl}/api/alumni/news-curation/google`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Google News articles');
      }

      const result = await response.json();
      setGoogleNewsData(result.data);

    } catch (err) {
      console.error('Error fetching Google News:', err);
      setError('Failed to load news articles. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchYouTube = async (forceRefresh = false) => {
    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

      const url = forceRefresh
        ? `${backendUrl}/api/alumni/news-curation/youtube?force_refresh=true`
        : `${backendUrl}/api/alumni/news-curation/youtube`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch YouTube videos');
      }

      const result = await response.json();
      setYoutubeData(result.data);

    } catch (err) {
      console.error('Error fetching YouTube:', err);
      setError('Failed to load videos. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

      const endpoint = activeTab === 'google_news'
        ? '/api/alumni/news-curation/google/refresh'
        : '/api/alumni/news-curation/youtube/refresh';

      const response = await fetch(`${backendUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to refresh content');
      }

      const result = await response.json();

      // Update data and refresh status
      if (activeTab === 'google_news') {
        setGoogleNewsData(result.data);
      } else {
        setYoutubeData(result.data);
      }

      await fetchRefreshStatus();

    } catch (err) {
      console.error('Error refreshing:', err);
      setError(err.message || 'Failed to refresh content. You may have reached your daily limit.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const openShareModal = (content, type) => {
    setSelectedContent({ ...content, type });
    setCommentary('');
    setShareSuccess(false);
    setShareMessage('');
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setSelectedContent(null);
    setCommentary('');
    setShareSuccess(false);
    setShareMessage('');
  };

  const handleShare = async () => {
    if (!selectedContent) return;

    setIsSharing(true);
    setShareMessage('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

      const shareData = {
        source_type: selectedContent.type === 'article' ? 'google_news' : 'youtube',
        source_url: selectedContent.type === 'article' ? selectedContent.link : selectedContent.url,
        source_title: selectedContent.title,
        source_thumbnail: selectedContent.type === 'article' ? selectedContent.image : selectedContent.thumbnail,
        commentary: commentary.trim() || null
      };

      const response = await fetch(`${backendUrl}/api/alumni/news-curation/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(shareData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to share content');
      }

      const result = await response.json();

      setShareSuccess(true);

      if (result.requires_approval) {
        setShareMessage('Your post has been submitted for approval and will be visible once approved by an administrator.');
      } else {
        setShareMessage('Your post has been shared to the network!');
      }

      // Mark as shared
      const contentId = selectedContent.type === 'article'
        ? selectedContent.link
        : selectedContent.video_id;
      setSharedContentIds(new Set([...sharedContentIds, contentId]));

      // Close modal after delay
      setTimeout(() => {
        closeShareModal();
      }, 2500);

    } catch (err) {
      console.error('Error sharing content:', err);
      setShareMessage(err.message || 'Failed to share content. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const getRefreshButtonText = () => {
    if (!refreshStatus) return 'Refresh';

    const status = activeTab === 'google_news'
      ? refreshStatus.google_news
      : refreshStatus.youtube;

    if (!status) return 'Refresh';

    if (status.is_superadmin) {
      return 'Refresh';
    }

    if (!status.allowed) {
      return 'Limit Reached';
    }

    return `Refresh (${status.remaining} left today)`;
  };

  const isRefreshDisabled = () => {
    if (!refreshStatus) return true;

    const status = activeTab === 'google_news'
      ? refreshStatus.google_news
      : refreshStatus.youtube;

    if (!status) return true;

    return !status.allowed && !status.is_superadmin;
  };

  const getFilteredContent = () => {
    const data = activeTab === 'google_news' ? googleNewsData : youtubeData;

    if (!data || !data.topics) return [];

    if (selectedTopic === 'all') {
      return data.topics;
    }

    return data.topics.filter(topic => topic.topic_id === parseInt(selectedTopic));
  };

  const isContentShared = (content, type) => {
    const contentId = type === 'article' ? content.link : content.video_id;
    return sharedContentIds.has(contentId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img
                src={nacmeLogo}
                alt="NACME"
                className="h-10 w-auto cursor-pointer"
                onClick={() => navigate(PATHS.dashboard)}
              />
              <h1 className="text-xl font-bold text-gray-900">Content Curation</h1>
            </div>

            <button
              onClick={() => navigate(PATHS.dashboard)}
              className="text-gray-600 hover:text-gray-900 font-medium"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Access denied message */}
      {error && !isLoading && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <FiAlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Main content */}
      {!error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Controls */}
          <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              {/* Tabs */}
              <div className="flex space-x-2">
                <button
                  onClick={() => setActiveTab('google_news')}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors ${
                    activeTab === 'google_news'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <FiFileText size={18} />
                  <span>Google News</span>
                </button>
                <button
                  onClick={() => setActiveTab('youtube')}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors ${
                    activeTab === 'youtube'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <FiYoutube size={18} />
                  <span>YouTube</span>
                </button>
              </div>

              {/* Topic filter and refresh button */}
              <div className="flex items-center space-x-3">
                <select
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Topics</option>
                  {topics
                    .filter(topic => topic.topic_type === (activeTab === 'google_news' ? 'google_news' : 'youtube'))
                    .map(topic => (
                      <option key={topic.id} value={topic.id}>
                        {topic.topic_name}
                      </option>
                    ))}
                </select>

                <button
                  onClick={handleManualRefresh}
                  disabled={isRefreshing || isRefreshDisabled()}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors ${
                    isRefreshing || isRefreshDisabled()
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  <FiRefreshCw
                    size={18}
                    className={isRefreshing ? 'animate-spin' : ''}
                  />
                  <span>{getRefreshButtonText()}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Content grid */}
          {!isLoading && (
            <div className="space-y-8">
              {getFilteredContent().map((topic) => (
                <div key={topic.topic_id}>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {topic.topic_name}
                  </h2>

                  {/* Google News articles */}
                  {activeTab === 'google_news' && topic.articles && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {topic.articles.map((article, idx) => (
                        <ArticleCard
                          key={`${article.link}-${idx}`}
                          article={article}
                          onShare={() => openShareModal(article, 'article')}
                          isShared={isContentShared(article, 'article')}
                          formatDate={formatDate}
                        />
                      ))}
                    </div>
                  )}

                  {/* YouTube videos */}
                  {activeTab === 'youtube' && topic.videos && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {topic.videos.map((video, idx) => (
                        <VideoCard
                          key={`${video.video_id}-${idx}`}
                          video={video}
                          onShare={() => openShareModal(video, 'video')}
                          isShared={isContentShared(video, 'video')}
                          formatDate={formatDate}
                        />
                      ))}
                    </div>
                  )}

                  {/* No content message */}
                  {activeTab === 'google_news' && (!topic.articles || topic.articles.length === 0) && (
                    <p className="text-gray-500 text-center py-8">No articles found for this topic.</p>
                  )}
                  {activeTab === 'youtube' && (!topic.videos || topic.videos.length === 0) && (
                    <p className="text-gray-500 text-center py-8">No videos found for this topic.</p>
                  )}
                </div>
              ))}

              {getFilteredContent().length === 0 && (
                <div className="text-center py-20">
                  <p className="text-gray-500 text-lg">No topics configured yet. Contact an administrator to set up curation topics.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Share to Network</h3>
                <button
                  onClick={closeShareModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>

              {/* Content preview */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex space-x-4">
                  {(selectedContent.image || selectedContent.thumbnail) && (
                    <img
                      src={selectedContent.image || selectedContent.thumbnail}
                      alt={selectedContent.title}
                      className="w-32 h-24 object-cover rounded flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                      {selectedContent.title}
                    </h4>
                    {selectedContent.type === 'article' && selectedContent.source && (
                      <p className="text-sm text-gray-600">Source: {selectedContent.source}</p>
                    )}
                    {selectedContent.type === 'video' && selectedContent.channel_name && (
                      <p className="text-sm text-gray-600">Channel: {selectedContent.channel_name}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Commentary input */}
              {!shareSuccess && (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add your commentary (optional)
                  </label>
                  <textarea
                    value={commentary}
                    onChange={(e) => setCommentary(e.target.value)}
                    placeholder="Share your thoughts about this content..."
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    disabled={isSharing}
                  />
                </>
              )}

              {/* Success/error message */}
              {shareMessage && (
                <div className={`mt-4 p-4 rounded-lg flex items-start space-x-3 ${
                  shareSuccess
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}>
                  {shareSuccess ? (
                    <FiCheck className="text-green-600 mt-0.5 flex-shrink-0" size={20} />
                  ) : (
                    <FiAlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
                  )}
                  <p className={shareSuccess ? 'text-green-800' : 'text-red-800'}>
                    {shareMessage}
                  </p>
                </div>
              )}

              {/* Action buttons */}
              {!shareSuccess && (
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={closeShareModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                    disabled={isSharing}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleShare}
                    disabled={isSharing}
                    className={`px-6 py-2 rounded-lg font-medium text-white ${
                      isSharing
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isSharing ? 'Sharing...' : 'Post Now'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Article Card Component
const ArticleCard = ({ article, onShare, isShared, formatDate }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
      {article.image && (
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-48 object-cover rounded-t-lg"
        />
      )}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
          {article.title}
        </h3>

        <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
          <span>{article.source}</span>
          <span>{formatDate(article.publishedAt)}</span>
        </div>

        <div className="flex space-x-2">
          <a
            href={article.link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-center flex items-center justify-center space-x-2"
          >
            <FiExternalLink size={16} />
            <span>View Article</span>
          </a>

          {!isShared ? (
            <button
              onClick={onShare}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Share
            </button>
          ) : (
            <div className="flex-1 px-4 py-2 bg-green-100 text-green-800 rounded-lg font-medium text-center flex items-center justify-center space-x-2">
              <FiCheck size={16} />
              <span>Shared</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Video Card Component
const VideoCard = ({ video, onShare, isShared, formatDate }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
      {video.thumbnail && (
        <div className="relative">
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full h-48 object-cover rounded-t-lg"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-t-lg">
            <FiYoutube size={48} className="text-white opacity-80" />
          </div>
        </div>
      )}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
          {video.title}
        </h3>

        <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
          <span className="line-clamp-1">{video.channel_name}</span>
          <span>{formatDate(video.publication_date)}</span>
        </div>

        <div className="flex space-x-2">
          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-center flex items-center justify-center space-x-2"
          >
            <FiExternalLink size={16} />
            <span>Watch Video</span>
          </a>

          {!isShared ? (
            <button
              onClick={onShare}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
            >
              Share
            </button>
          ) : (
            <div className="flex-1 px-4 py-2 bg-green-100 text-green-800 rounded-lg font-medium text-center flex items-center justify-center space-x-2">
              <FiCheck size={16} />
              <span>Shared</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlumniNewsCuration;
